/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit-log/types/organisation.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React from 'react';
import LogoAudit from '@pages/admin/audit-log/logo-audit';
import { GetMimeType } from 'views/components/core/edit-logo/image-service';
import { FieldChangeGrid } from 'views/pages/admin/audit-log/change-grid';
import AuditEnumValues from 'views/pages/admin/audit-log/definitions/enums';
import { getFieldChangeItems, getProcessedFieldChangeItems } from 'views/pages/admin/audit-log/service';

const OrganisationRecordChangeGrid = ({ oldRecord, newRecord, oldItem, newItem }: any) => {
    const enums: any = {
        Type: AuditEnumValues.OrgType,
    };

    const lookups: any = {
        Logo: (org: any, recordData: any) => {
            const base64 = recordData?.Logo?.$binary?.base64 as string | undefined;

            if (base64 == null || base64.length === 0) {
                return 'None';
            }

            const mimeType = GetMimeType(base64);
            if (mimeType == null) {
                return base64;
            }

            return `data:${mimeType};base64,${base64}`;
        },
        LogoId: (_: any, recordData: any) => <LogoAudit logoId={recordData?.LogoId?.$oid} />,
    };

    const listOfObjects: any = {
        Security: [
            'AccountInactiveDays',
            'LockoutPeriodMinutes',
            'MinLowercaseCount',
            'MinNumberCount',
            'MinPasswordLength',
            'MinSpecialCharCount',
            'MinUppercaseCount',
            'NumAttemptsBeforeLockout',
            'TwoFactorEnabled',
            'UserIdleTimeoutMinutes',
        ],
        PrimaryContact: ['Email', 'FirstName', 'LastName', 'Mobile', 'Phone'],
    };

    const associations: any = {
        Modules: {
            itemField: 'modules',
            name: 'Module',
            nameResolver: (moduleName: any) => moduleName,
        },
        Products: {
            itemField: 'products',
            name: 'Products',
            nameResolver: (product: any) => product?.name,
        },
        Programs: {
            itemField: 'programs',
            name: 'Programs',
            nameResolver: (program: any) => program?.name,
        },
        Studies: {
            itemField: 'studies',
            name: 'Studies',
            nameResolver: (study: any) => study?.shortName ?? study?.studyNumber,
        },
        Roles: {
            itemField: 'roles',
            name: 'Roles',
            nameResolver: (role: any) => role?.name,
        },
        Members: {
            itemField: 'members',
            name: 'Members',
            nameResolver: (member: any) => `${member?.lastName ?? ''} ${member?.firstName ?? ''}`,
        },
        EvaluationModels: {
            itemField: 'evaluationModels',
            name: 'Evaluation Models',
            nameResolver: (model: any) => model?.name,
        },
    };

    const items = getFieldChangeItems(oldRecord, newRecord, null, (field: string) =>
        getProcessedFieldChangeItems({
            oldRecord,
            newRecord,
            oldItem,
            newItem,
            field,
            lookups,
            associations,
            enums,
            listOfObjects,
        }),
    );

    return <FieldChangeGrid items={items} />;
};

export { OrganisationRecordChangeGrid };
